/**
 * Hook para definir las columnas de la tabla de inventario de productos
 * Hook to define columns for inventory products table
 */
import { Product } from '@shared/components/ui/Product';
import { InfoTooltip } from '@shared/components/ui/InfoTooltip';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { InventoryProduct } from '../models/InventoryApiResponse';

interface UseAgGridColumnsProps {
  selectedColumns?: string[];
}

export const useAgGridColumns = ({ selectedColumns = [] }: UseAgGridColumnsProps) => {
  const { t } = useTranslation('inventoryModule');
  // 🎯 Obtener código de moneda desde /me - Get currency code from /me
  const currency = useCurrency();

  // Definiciones completas de columnas para AG Grid
  // Complete column definitions for AG Grid
  const allColumnDefs: ColDef[] = useMemo(() => {
    // Función auxiliar para construir URL completa de imagen
    // Helper function to build complete image URL
    const buildImageUrl = (imageHash: string | null | undefined): string => {
      if (!imageHash) return '/imagen/example.webp';
      if (imageHash.startsWith('http')) return imageHash;
      return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageHash}`;
    };
    const baseColumns: CustomColDef[] = [
      // Columna: Producto - Product column
      {
        field: 'name',
        headerName: t('table.product'),
        sortable: true,
        cellRenderer: (params: any) => {
          const data = params.data as InventoryProduct;
          if (!data) return '-';

          return (
            <Product
              id={data.id}
              name={data.name}
              src={buildImageUrl(data.image_hash)}
              code={data.code}
              showImageModal={true}
            />
          );
        },
        minWidth: 250,
        suppressHeaderMenuButton: true,
      },
      // Columna: Stock disponible - Available stock column
      {
        field: 'stock_available',
        headerComponent: () => (
          <div className="flex items-center gap-2">
            <span className="text-title">{t('table.stock_available')}</span>
            <InfoTooltip text={t('tooltips.stock_available')} dataTestId="stock-available-tooltip" position="bottom" />
          </div>
        ),
        sortable: true,
        cellRenderer: (params: any) => {
          const data = params.data as InventoryProduct;
          const stock = data?.stock_available ?? 0;
          return <span className="text-title">{stock}</span>;
        },
        minWidth: 140,
        suppressHeaderMenuButton: true,
      },
      // Columna: Valor inventario - Inventory value column
      {
        field: 'inventory_value',
        headerComponent: () => (
          <div className="flex items-center gap-2">
            <span className="text-title">{t('table.inventory_value')}</span>
            <InfoTooltip text={t('tooltips.inventory_value')} dataTestId="inventory-value-tooltip" position="bottom" />
          </div>
        ),
        sortable: true,
        cellRenderer: (params: any) => {
          const data = params.data as InventoryProduct;
          const value = data?.inventory_value ?? 0;
          return <span className="text-title">{formatCurrencyAmount(value, currency)}</span>;
        },
        minWidth: 150,
        suppressHeaderMenuButton: true,
      },
      // Columna: Ventas promedio diario - Average daily sales column
      {
        field: 'avg_daily_sales',
        headerComponent: () => (
          <div className="flex items-center gap-2">
            <span className="text-title">{t('table.avg_daily_sales')}</span>
            <InfoTooltip text={t('tooltips.avg_daily_sales')} dataTestId="avg-daily-sales-tooltip" position="bottom" />
          </div>
        ),
        sortable: true,
        cellRenderer: (params: any) => {
          const data = params.data as InventoryProduct;
          const avgSales = data?.avg_daily_sales ?? 0;
          return <span className="text-title">{avgSales.toFixed(2)}</span>;
        },
        minWidth: 160,
        suppressHeaderMenuButton: true,
      },
      // Columna: Días de stock - Days of stock column
      {
        field: 'days_of_stock_remaining',
        headerComponent: () => (
          <div className="flex items-center gap-2">
            <span className="text-title">{t('table.days_of_stock')}</span>
            <InfoTooltip text={t('tooltips.days_of_stock')} dataTestId="days-of-stock-tooltip" position="bottom" />
          </div>
        ),
        sortable: true,
        cellRenderer: (params: any) => {
          const data = params.data as InventoryProduct;
          const days = data?.days_of_stock_remaining;
          if (days === null || days === undefined) {
            return <span className="text-complementary-text">-</span>;
          }
          return <span className="text-title">{days}</span>;
        },
        minWidth: 140,
        suppressHeaderMenuButton: true,
      },
    ];

    // Filtrar columnas según selectedColumns si se proporciona
    // Filter columns according to selectedColumns if provided
    if (selectedColumns.length > 0) {
      return baseColumns.filter((col) => {
        const field = col.field || '';
        return selectedColumns.includes(field);
      });
    }

    return baseColumns;
  }, [t, selectedColumns, currency]);

  return {
    allColumnDefs,
    filteredColumnDefs: allColumnDefs,
  };
};
